import { ConnectParams, IntegrationWorkflowState, paragon } from '@useparagon/connect';
import { camelCase, debounce } from 'lodash';
import { useEffect, useRef } from 'react';
import useParagonUser from './useParagonUser';
import { useParagonCache } from './useParagonCache';
import type {
	ParagonIntegrationWithUpsalesMeta,
	ParagonIntegrationConfiguration,
	ParagonConnectionMetadata,
	ParagonIntegrationField
} from './paragonTypes';
import openModal from 'App/services/Modal';
import { combineUserWithIntegration } from './paragonHelpers';
import logError from 'Helpers/logError';
import { makeCancelable, type CancelablePromise } from '@upsales/components/Utils/CancelablePromise';
import type {
	ProvidedByExternalIntegrations,
	FetchedIntegrations,
	ExternalIntegrationWorkflowErrorHandling,
	DataSourceSettings,
	DataSource
} from '../externalIntegrationTypes';
import ExternalIntegration from 'App/resources/ExternalIntegration';
import ExternalIntegrationSettingsResource from 'App/resources/ExternalIntegrationSettings';
import ExternalIntegrationWorkflowErrorHandlingResource from 'App/resources/ExternalIntegrationWorkflowErrorHandling';
import { useAppDispatch, useSelector } from 'App/components/hooks';
import { useTranslation } from 'Components/Helpers/translate';
import { setIntegrations } from 'Store/reducers/ExternalIntegrationReducer';
import NotificationService from 'App/babel/NotificationService';

const useParagonIntegrations = (): Omit<
	ProvidedByExternalIntegrations,
	'availableIntegrations' | 'connectedIntegrations' | 'initialised'
> & {
	user?: any;
	setConnectionMetadataOnUserMeta?: (
		integrationType: string,
		configurationId: string,
		connectionMetadata: any
	) => Promise<void>;
	removeConnectionMetadataOnUserMeta?: (integrationType: string, configurationIds: string[]) => Promise<void>;
} => {
	const dispatch = useAppDispatch();
	const { availableIntegrations, connectedIntegrations, allIntegrations, tokens } = useSelector(
		state => state.ExternalIntegration
	);
	const { user, setConnectionMetadataOnUserMeta, removeConnectionMetadataOnUserMeta } = useParagonUser();
	const { t } = useTranslation();

	const pendingFetch = useRef<CancelablePromise<FetchedIntegrations> | null>(null);
	const integrationsCache = useParagonCache<FetchedIntegrations>('integrations');
	const isFetchingRef = useRef(false);
	const fetchDebounceRef = useRef<NodeJS.Timeout | null>(null);

	const fieldValueQueueRef = useRef<{
		integrationType?: ParagonIntegrationWithUpsalesMeta['type'];
		settings?: { selectedCredentialId?: string; selectedConfigurationId?: string };
		values: Record<string, any>;
	}>({ values: {} });
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	const fetchIntegrations = ({ skipCache }: { skipCache?: boolean } = {}): CancelablePromise<FetchedIntegrations> => {
		if (!skipCache) {
			if (integrationsCache.isCacheValid() && integrationsCache.getCacheData()) {
				dispatch(setIntegrations(integrationsCache.getCacheData()!));
				return makeCancelable<FetchedIntegrations>(Promise.resolve(integrationsCache.getCacheData()!));
			}

			const existingPromise = integrationsCache.getCachePromise();
			if (existingPromise) {
				return existingPromise;
			}
		}

		if (pendingFetch.current) {
			pendingFetch.current.cancel();
			pendingFetch.current = null;
		}

		const cancellable = makeCancelable<FetchedIntegrations>(ExternalIntegration.getIntegrations('paragon'));

		integrationsCache.setCachePromise(cancellable);
		pendingFetch.current = cancellable;

		cancellable.promise
			.then(integrations => {
				if (!integrations) {
					logError(new Error('FAILED_TO_FETCH_INTEGRATIONS'), 'Error fetching Paragon integrations');
					dispatch(setIntegrations(null));
					return;
				}
				integrationsCache.setCacheData(integrations);
				dispatch(setIntegrations(integrations));
			})
			.catch(err => {
				logError(err, 'Error fetching Paragon integrations');
				dispatch(setIntegrations(null));
			})
			.finally(() => {
				pendingFetch.current = null;
				if (integrationsCache.getCachePromise() === cancellable) {
					integrationsCache.clearCachePromise();
				}
			});

		return cancellable;
	};

	useEffect(() => {
		if (user && tokens?.token && !isFetchingRef.current) {
			if (availableIntegrations?.length > 0 || connectedIntegrations?.length > 0) {
				return;
			}

			if (integrationsCache.isCacheValid() && integrationsCache.getCacheData()) {
				return;
			}

			if (integrationsCache.getCachePromise()) {
				return;
			}

			if (fetchDebounceRef.current) {
				clearTimeout(fetchDebounceRef.current);
			}

			fetchDebounceRef.current = setTimeout(() => {
				isFetchingRef.current = true;
				const fetchPromise = fetchIntegrations();
				fetchPromise.promise
					.then(() => {
						isFetchingRef.current = false;
					})
					.catch(() => {
						isFetchingRef.current = false;
					});
			}, 100);
		}
	}, [user, tokens?.token, availableIntegrations?.length, connectedIntegrations?.length]);

	useEffect(() => {
		return () => {
			pendingFetch.current?.cancel();
			pendingFetch.current = null;
			if (fetchDebounceRef.current) {
				clearTimeout(fetchDebounceRef.current);
				fetchDebounceRef.current = null;
			}
		};
	}, []);

	const updateIntegration = async (integration: ParagonIntegrationWithUpsalesMeta, configurationId?: string) => {
		const updatedUser = paragon.getUser();
		if (updatedUser.authenticated) {
			const updatedIntegration = combineUserWithIntegration(integration, updatedUser);
			integrationsCache.clearCachePromise();
			fetchIntegrations({ skipCache: true });
			return { updatedIntegration, configurationId, installed: true };
		} else {
			integrationsCache.clearCachePromise();
			fetchIntegrations({ skipCache: true });
			return { updatedIntegration: integration, configurationId, installed: true };
		}
	};

	const openIntegrationConfiguration = (
		integrationType: ParagonIntegrationWithUpsalesMeta['type'],
		configurationId: ParagonIntegrationWithUpsalesMeta['configurations'][0]['id']
	) => {
		return new Promise((resolve, reject) => {
			paragon.connect(integrationType, {
				selectedCredentialId: configurationId,
				onClose: (event, user) => {
					const installedCredential = user.integrations[integrationType]?.allCredentials.find(c => {
						return c.id === configurationId;
					});
					resolve(installedCredential || null);
				}
			});
		});
	};

	const installIntegration = async (
		integration: ParagonIntegrationWithUpsalesMeta,
		connectionMetadata?: ParagonConnectionMetadata
	) => {
		const options: ConnectParams = {};
		if (integration.multiAccount) {
			options.allowMultipleCredentials = true;
			options.showPortalAfterInstall = false;
		}
		const res = await paragon.installIntegration(integration.type, options);
		if (!res?.credentialId) {
			return { updatedIntegration: integration, configurationId: res?.credentialId, installed: false };
		}

		if (connectionMetadata) {
			await setConnectionMetadataOnUserMeta(integration.type, res.credentialId, connectionMetadata);
		}
		if (integration.multiAccount) {
			const installed = await openIntegrationConfiguration(integration.type, res.credentialId);
			if (!installed) {
				return { updatedIntegration: integration, configurationId: res.credentialId, installed: false };
			}
		}
		return updateIntegration(integration, res.credentialId);
	};

	const updateConnection = async (
		integration: ParagonIntegrationWithUpsalesMeta,
		connectionMetadata: ParagonConnectionMetadata,
		connectionId: ParagonIntegrationConfiguration['id']
	) => {
		await setConnectionMetadataOnUserMeta(integration.type, connectionId, connectionMetadata);
		await fetchIntegrations({ skipCache: true });
		return updateIntegration(integration, connectionId);
	};

	const uninstallIntegration = async (integration: ParagonIntegrationWithUpsalesMeta) => {
		if (!integration.multiAccount || !integration.configurations?.length) {
			await paragon.uninstallIntegration(integration.type, {});
			await fetchIntegrations({ skipCache: true });
			return;
		}

		const uninstalledIntegrationIds = [];
		for (const configuration of integration.configurations) {
			await paragon.uninstallIntegration(integration.type, {
				selectedCredentialId: configuration.id
			});
			uninstalledIntegrationIds.push(configuration.id);
		}
		await removeConnectionMetadataOnUserMeta(integration.type, uninstalledIntegrationIds);
		await fetchIntegrations({ skipCache: true });
	};

	const disconnectConnection = async (
		integration: ParagonIntegrationWithUpsalesMeta,
		selectedCredentialId: string
	) => {
		await paragon.uninstallIntegration(integration.type, {
			selectedCredentialId
		});
		await removeConnectionMetadataOnUserMeta(integration.type, [selectedCredentialId]);
		await fetchIntegrations({ skipCache: true });
		return updateIntegration(integration, selectedCredentialId);
	};

	const reconnectIntegration = (
		integration: ParagonIntegrationWithUpsalesMeta,
		selectedCredentialId: string
	): Promise<void> => {
		return new Promise((resolve, reject) => {
			paragon.startOAuthFlow(
				integration.type,
				{ selectedCredentialId },
				{
					onSuccess: async () => {
						await fetchIntegrations({ skipCache: true });
						resolve();
					},
					onError: error => {
						reject(error);
					}
				}
			);
		});
	};

	const openIntegration = async (integration: ParagonIntegrationWithUpsalesMeta, globalMode?: boolean) => {
		openModal('ExternalIntegrationModal', {
			integration,
			globalMode
		});
	};

	const showSaveNotification = (isError: boolean) => {
		const notification = {
			style: isError ? 'error' : 'success',
			icon: isError ? 'times' : 'check',
			title: isError ? t('default.saveSettingsFailed') : t('default.saveSettingsSuccessful')
		};

		NotificationService.add(notification);
	};

	const saveGlobalSettings = async (
		integration: ParagonIntegrationWithUpsalesMeta,
		settings: {
			id?: number;
			multiAccount: boolean;
			userOnly: boolean;
			healthCheckEndpoint?: string;
			initSyncWorkflowId?: string;
		}
	) => {
		await ExternalIntegrationSettingsResource.save({
			id: integration.globalSettings?.id,
			externalId: integration.type,
			...settings
		});

		// Only clear cache promise, not the data, since we're about to refetch
		integrationsCache.clearCachePromise();
		fetchIntegrations({ skipCache: true });
	};

	const saveWorkflowErrorHandlings = async (
		integration: ParagonIntegrationWithUpsalesMeta,
		errorHandlingsToSave: ExternalIntegrationWorkflowErrorHandling[],
		errorHandlingsToRemove: ExternalIntegrationWorkflowErrorHandling[]
	) => {
		const idsToRemove = errorHandlingsToRemove
			.filter(errorHandling => errorHandling.id !== undefined)
			.map(errorHandling => errorHandling.id) as number[];

		if (idsToRemove.length > 0) {
			await ExternalIntegrationWorkflowErrorHandlingResource.deleteMultiple(idsToRemove, {
				// No need to show notification if we're saving new error handlings since this will result in two notifications
				skipNotification: errorHandlingsToSave.length > 0
			});
		}

		if (errorHandlingsToSave.length > 0) {
			const { data: savedErrorHandlings } = await ExternalIntegrationWorkflowErrorHandlingResource.save(
				errorHandlingsToSave
			);

			if (!savedErrorHandlings) {
				return { updatedIntegration: integration };
			}

			// Internal update to integration to reflect the new error handlings
			integration.workflows.forEach(workflow => {
				const errorHandlingUpdate = savedErrorHandlings.some(
					errorHandling => errorHandling.externalWorkflowId === workflow.id
				);
				if (errorHandlingUpdate) {
					workflow.errorHandlings = savedErrorHandlings.filter(
						errorHandling => errorHandling.externalWorkflowId === workflow.id
					);
				}
			});

			// Update global error handlings
			const globalErrorHandlings = savedErrorHandlings.filter(
				errorHandling => errorHandling.externalWorkflowId === 'all'
			);
			if (globalErrorHandlings.length > 0) {
				integration.errorHandlings = globalErrorHandlings;
			}
		} else if (errorHandlingsToRemove.length > 0) {
			// If only removing error handlings, update the integration accordingly
			const globalRemovals = errorHandlingsToRemove.filter(
				errorHandling => errorHandling.externalWorkflowId === 'all'
			);
			if (globalRemovals.length > 0 && integration.errorHandlings) {
				integration.errorHandlings = integration.errorHandlings.filter(
					eh => !errorHandlingsToRemove.some(removed => removed.id === eh.id)
				);
			}
		}

		return { updatedIntegration: integration };
	};

	const getFieldType = (type: string): ParagonIntegrationField['type'] => {
		const fieldTypeMap = {
			DYNAMIC_ENUM: 'select',
			CUSTOM_DROPDOWN: 'select',
			NUMBER: 'number',
			TEXT_NO_VARS: 'text',
			EMAIL: 'email',
			URL: 'url',
			BOOLEAN_INPUT: 'toggle',
			PASSWORD: 'password'
		};

		const fieldType = fieldTypeMap[type as keyof typeof fieldTypeMap];

		return (fieldType ?? 'text') as ParagonIntegrationField['type'];
	};

	const getDataSourceOptions = async (integration: ParagonIntegrationWithUpsalesMeta['type'], action: string) => {
		const dataSourceOptions = await paragon.getFieldOptions({
			integration,
			action,
			parameters: []
		});

		return dataSourceOptions.data.map(option => ({
			id: option.value,
			title: option.label
		}));
	};

	const processInputFields = async (
		inputFields: any[],
		integration: ParagonIntegrationWithUpsalesMeta,
		getStoredValue: (fieldId: string) => any
	): Promise<ParagonIntegrationField[]> => {
		const fields: ParagonIntegrationField[] = [];
		for (const inputField of inputFields) {
			// Check if title contains underscore and split accordingly
			let title = inputField.title;
			let section: string | undefined;

			if (inputField.title.includes('_')) {
				const parts = inputField.title.split('_');
				section = parts[0];
				title = parts.slice(1).join('_'); // Join remaining parts in case there are multiple underscores
			}

			const field: ParagonIntegrationField = {
				id: inputField.id,
				type: getFieldType(inputField.type),
				title,
				...(section && { section })
			};

			if (field.type === 'select') {
				if (inputField.sourceType) {
					try {
						field.options = await getDataSourceOptions(integration.type, inputField.sourceType);
					} catch (error) {
						logError(error, 'Error fetching data source options');
					}
				} else {
					field.options =
						inputField.customDropdownOptions?.map((option: { label: string; value: string }) => ({
							title: option.label,
							id: option.value
						})) ?? [];
				}
			}
			if (inputField.key && inputField.key.startsWith('upsales')) {
				const parts = inputField.key.split('-');
				parts.shift(); // Remove 'upsales'
				let isMulti = false;
				if (parts[parts.length - 1] === 'multi') {
					isMulti = true;
					parts.pop();
				}
				const subType = parts
					.map((part: string, idx: number) =>
						idx === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
					)
					.join('');

				field.subType = subType as ParagonIntegrationField['subType'];
				if (field.type === 'select' && isMulti) {
					field.type = 'multiSelect';
				}
			}

			field.storedValue = getStoredValue(field.id);
			field.tooltip = inputField.tooltip;
			field.required = inputField.required;

			fields.push(field);
		}

		return fields;
	};

	const getFields = async (
		integration: ParagonIntegrationWithUpsalesMeta | undefined,
		integrationType?: ParagonIntegrationWithUpsalesMeta['type']
	): Promise<ParagonIntegrationField[]> => {
		if (!integration && !integrationType) {
			return [];
		}
		if (!integration) {
			integration = allIntegrations.find(i => i.type === integrationType);
		}
		if (!integration) {
			return [];
		}

		const inputFields = integration.configs?.[0]?.values?.sharedMeta?.inputs ?? [];
		if (inputFields.length === 0) {
			return [];
		}

		const getStoredValue = (fieldId: string) => {
			const configurations = integration?.configurations?.[0] as any; // seems like there is a typescript issue within paragon, where sharedSettings is not where specified
			const sharedSettings =
				configurations?.config?.sharedSettings ?? configurations?.configurations?.[0]?.sharedSettings;
			return sharedSettings?.[fieldId];
		};

		return processInputFields(inputFields, integration, getStoredValue);
	};

	const getFieldsWithValues = async (
		integration: ParagonIntegrationWithUpsalesMeta | undefined,
		integrationType?: ParagonIntegrationWithUpsalesMeta['type']
	): Promise<Record<string, any>> => {
		const fields = await getFields(integration, integrationType);
		return fields.reduce((acc, field) => {
			acc[camelCase(field.title)] = field.storedValue;
			return acc;
		}, {} as Record<string, any>);
	};

	const getWorkflowSettings = (
		workflowId: string,
		integration: ParagonIntegrationWithUpsalesMeta
	): IntegrationWorkflowState => {
		return (
			integration.configurations?.[0]?.configurations?.[0]?.workflowSettings?.[workflowId] ??
			({} as IntegrationWorkflowState)
		);
	};

	const getWorkflowFields = async (
		integration: ParagonIntegrationWithUpsalesMeta | undefined,
		workflowId: string,
		integrationType?: ParagonIntegrationWithUpsalesMeta['type']
	): Promise<ParagonIntegrationField[]> => {
		if (!integration && !integrationType) {
			return [];
		}
		if (!integration) {
			integration = allIntegrations.find(i => i.type === integrationType);
		}
		if (!integration || !workflowId) {
			return [];
		}

		const workflowMeta = integration.configs?.[0]?.values?.workflowMeta?.[workflowId];
		if (!workflowMeta?.inputs) {
			return [];
		}

		const inputFields = workflowMeta.inputs;
		if (inputFields.length === 0) {
			return [];
		}

		const getStoredValue = (fieldId: string) => {
			const workflowSettings = getWorkflowSettings(workflowId, integration);
			return workflowSettings.settings?.[fieldId];
		};

		return processInputFields(inputFields, integration, getStoredValue);
	};

	const changeFieldValue = async (
		integrationType: ParagonIntegrationWithUpsalesMeta['type'],
		inputId: string,
		value: any,
		settings?: { selectedCredentialId?: string; selectedConfigurationId?: string }
	) => {
		// Add/overwrite the value in the queue
		if (
			!fieldValueQueueRef.current.integrationType ||
			fieldValueQueueRef.current.integrationType !== integrationType ||
			JSON.stringify(fieldValueQueueRef.current.settings) !== JSON.stringify(settings)
		) {
			// If integrationType/settings changed, reset the queue
			fieldValueQueueRef.current = { integrationType, settings, values: {} };
		}
		fieldValueQueueRef.current.values[inputId] = value;

		// Reset debounce timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		return new Promise((resolve, reject) => {
			debounceTimerRef.current = setTimeout(async () => {
				const { integrationType, values, settings } = fieldValueQueueRef.current;
				try {
					// Seems to be an issue here that the result.userState does not reflect the changes
					const result = await paragon.updateIntegrationUserSettings(integrationType!, values, settings);
					await fetchIntegrations({ skipCache: true }).promise;
					showSaveNotification(false);
					// Clear the queue after successful update
					fieldValueQueueRef.current = { values: {} };
					resolve(result);
				} catch (error) {
					showSaveNotification(true);
					logError(error, 'Error updating integration user setting');
					reject(error);
				}
			}, 1500);
		});
	};

	const changeFieldValues = async (
		integrationType: ParagonIntegrationWithUpsalesMeta['type'],
		values: Record<string, any>,
		settings?: { selectedCredentialId?: string; selectedConfigurationId?: string }
	) => {
		// Add/overwrite the values in the queue
		if (
			!fieldValueQueueRef.current.integrationType ||
			fieldValueQueueRef.current.integrationType !== integrationType ||
			JSON.stringify(fieldValueQueueRef.current.settings) !== JSON.stringify(settings)
		) {
			// If integrationType/settings changed, reset the queue
			fieldValueQueueRef.current = { integrationType, settings, values: {} };
		}
		fieldValueQueueRef.current.values = values;
		// Reset debounce timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		return new Promise((resolve, reject) => {
			debounceTimerRef.current = setTimeout(async () => {
				const { integrationType, values, settings } = fieldValueQueueRef.current;
				if (!integrationType) {
					return;
				}

				try {
					const result = await paragon.updateIntegrationUserSettings(integrationType!, values, settings);
					showSaveNotification(false);
					await fetchIntegrations({ skipCache: true }).promise;

					// Clear the queue after successful update
					fieldValueQueueRef.current = { values: {} };
					resolve(result);
				} catch (error) {
					showSaveNotification(true);
					logError(error, 'Error updating integration user setting');
					reject(error);
				}
			}, 1500);
		});
	};

	// Store pending field updates for batching
	const pendingFieldUpdates = useRef<{
		integrationType?: ParagonIntegrationWithUpsalesMeta['type'];
		workflowId?: string;
		fields: Record<string, any>;
	}>({ fields: {} });

	// Promise-based debounced function
	const debouncedUpdatePromise = useRef<Promise<any> | null>(null);
	const debouncedUpdateResolve = useRef<((value: any) => void) | null>(null);
	const debouncedUpdateReject = useRef<((error: any) => void) | null>(null);

	// Helper function to clear promise references
	const clearPromiseReferences = () => {
		debouncedUpdateResolve.current = null;
		debouncedUpdateReject.current = null;
		debouncedUpdatePromise.current = null;
	};

	// Debounced function to update workflow field values
	const debouncedUpdateWorkflowField = useRef(
		debounce(async () => {
			const { integrationType, workflowId, fields } = pendingFieldUpdates.current;

			if (!integrationType || !workflowId || Object.keys(fields).length === 0) {
				// Resolve the promise with undefined if no fields to update
				if (debouncedUpdateResolve.current) {
					debouncedUpdateResolve.current(undefined);
					clearPromiseReferences();
				}
				return;
			}

			try {
				const result = await paragon.updateWorkflowUserSettings(integrationType, workflowId, fields);
				showSaveNotification(false);

				// Clear the pending updates after successful update
				pendingFieldUpdates.current = { fields: {} };

				// Resolve the promise with the result
				if (debouncedUpdateResolve.current) {
					debouncedUpdateResolve.current(result);
					clearPromiseReferences();
				}

				return result;
			} catch (error) {
				logError(error, 'Error updating workflow field value');

				// Reject the promise with the error
				if (debouncedUpdateReject.current) {
					debouncedUpdateReject.current(error);
					clearPromiseReferences();
				}

				throw error;
			}
		}, 500)
	).current;

	const changeWorkflowFieldValue = async (
		integrationType: ParagonIntegrationWithUpsalesMeta['type'],
		workflowId: string,
		inputId: string,
		value: any
	) => {
		// Store the field update in the pending updates
		pendingFieldUpdates.current = {
			integrationType,
			workflowId,
			fields: {
				...pendingFieldUpdates.current.fields,
				[inputId]: value
			}
		};

		// Create a new promise if one doesn't exist
		if (!debouncedUpdatePromise.current) {
			debouncedUpdatePromise.current = new Promise((resolve, reject) => {
				debouncedUpdateResolve.current = resolve;
				debouncedUpdateReject.current = reject;
			});
		}

		// Trigger the debounced update
		debouncedUpdateWorkflowField();

		// Return the promise
		return debouncedUpdatePromise.current;
	};

	// Cleanup debounced function on unmount
	useEffect(() => {
		return () => {
			debouncedUpdateWorkflowField.cancel();
			// Clear any pending updates
			pendingFieldUpdates.current = { fields: {} };
			// Clear promise references
			clearPromiseReferences();
		};
	}, [debouncedUpdateWorkflowField]);

	const updateWorkflowEnabled = async (
		integration: ParagonIntegrationWithUpsalesMeta,
		workflowId: string,
		enabled: boolean
	) => {
		try {
			const { errors } = await paragon.updateWorkflowState({ [workflowId]: enabled });
			showSaveNotification(errors?.length > 0);
			if (errors?.length) {
				for (const error of errors) {
					logError(error, 'Error updating workflow enabled status');
				}
				return { updatedIntegration: integration };
			}

			// Only clear cache promise, not the data, since we're about to refetch
			integrationsCache.clearCachePromise();
			const { availableIntegrations: freshAvailable, connectedIntegrations: freshConnected } =
				await fetchIntegrations({ skipCache: true }).promise;
			const all = [...freshConnected, ...freshAvailable];
			const updated = all.find(i => i.type === integration.type);
			return { updatedIntegration: updated || integration };
		} catch (error) {
			showSaveNotification(true);
			logError(error, 'Error updating workflow enabled status');
			throw error;
		}
	};

	const getDataSource = async (
		integrationType: ParagonIntegrationWithUpsalesMeta['type'],
		dataSource: DataSourceSettings
	) => {
		const dataSourceResults: Record<string, any> = {};
		for (const [name, dataSourceSettings] of Object.entries(dataSource)) {
			try {
				const { requestPath, requestMethod, requestBody, selectedCredentialId } = dataSourceSettings;

				// Validate required fields
				if (!requestPath || typeof requestPath !== 'string') {
					throw new Error('requestPath is required and must be a string');
				}

				if (!requestMethod || typeof requestMethod !== 'string') {
					throw new Error('requestMethod is required and must be a string');
				}

				const headers: Record<string, string> = {
					'Content-Type': 'application/json'
				};
				const init: any = {
					method: requestMethod,
					headers
				};

				const requestBodyContainsUnresolvedTags = requestBody?.includes('{{');
				if (requestBodyContainsUnresolvedTags) {
					continue;
				}

				if (requestBody && (requestMethod === 'POST' || requestMethod === 'PUT')) {
					init.body = requestBody;
				}
				if (selectedCredentialId) {
					init.selectedCredentialId = selectedCredentialId;
				}
				const response = await paragon.request<any>(integrationType, requestPath, init);
				dataSourceResults[name] = response;
			} catch (error) {
				logError(error, `Error fetching external data for data source: ${name}`);
				// Store error information for this specific data source instead of returning early
				dataSourceResults[name] = { error: (error as Error).message || 'Failed to fetch data' };
			}
		}

		return dataSourceResults;
	};

	const triggerSync = async (workflowId: string) => {
		try {
			await paragon.workflow(workflowId, {});
		} catch (error: any) {
			if (error.message) {
				let message = '';
				if (typeof error.message === 'string') {
					try {
						message = JSON.parse(error.message)?.message;
					} catch {
						message = error.message;
					}
				}
				const notification = {
					style: 'error',
					icon: 'times',
					title: t('default.integrations.syncFailed'),
					body: message
				};
				NotificationService.add(notification);
			}
			logError(error, 'Error triggering sync');
			throw error;
		}
	};

	// Helper function to check if a value is a tag (e.g., {{order.id}})
	const isTag = (value: string): boolean => {
		return /^\{\{.*\}\}$/.test(value);
	};

	const testDataSource = async (config: DataSource, integrationType: string, name: string) => {
		try {
			if (config.dataSourceType === 'upsales') {
				// For Upsales data sources, dynamically load the resource and call .get()
				if (!config.upsalesResource) {
					throw new Error('upsalesResource is required for Upsales data sources');
				}

				if (!config.upsalesId) {
					throw new Error('upsalesId is required for Upsales data sources');
				}

				// Import the helper function dynamically to avoid circular dependencies
				const { getResourceInstance } = await import('../ExternalIntegrationModal/helpers/getResourceInstance');
				const resourceInstance = getResourceInstance(config.upsalesResource);

				// Check if upsalesId is a tag (e.g., {{order.id}})
				if (isTag(config.upsalesId)) {
					// If it's a tag, use find with limit 1 and return the first item
					const response = await resourceInstance.find({ limit: 1 });
					return response.data[0] || null;
				} else {
					// If it's not a tag, use the original .get() method
					const response = await resourceInstance.get(config.upsalesId);
					return response.data;
				}
			} else {
				const response = await getDataSource(integrationType, { [name]: config });
				return response[name];
			}
		} catch (err: any) {
			throw new Error(err?.message || 'Unknown error');
		}
	};

	return {
		fetchIntegrations,
		openIntegration,
		openIntegrationConfiguration,
		installIntegration,
		uninstallIntegration,
		updateConnection,
		disconnectConnection,
		reconnectIntegration,
		saveGlobalSettings,
		saveWorkflowErrorHandlings,
		getFields,
		getWorkflowFields,
		changeFieldValue,
		changeFieldValues,
		changeWorkflowFieldValue,
		updateWorkflowEnabled,
		getWorkflowSettings,
		getDataSource,
		triggerSync,
		testDataSource,
		isTag,
		user,
		setConnectionMetadataOnUserMeta,
		removeConnectionMetadataOnUserMeta,
		getFieldsWithValues
	};
};

export default useParagonIntegrations;
